import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

const WebProjects = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const country = searchParams.get('country');
  const project = searchParams.get('project');

  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const countryDropdownRef = useRef(null);
  const projectDropdownRef = useRef(null);

  // Scroll to top when component mounts or when country/project changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [country, project]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
        setCountryDropdownOpen(false);
      }
      if (projectDropdownRef.current && !projectDropdownRef.current.contains(event.target)) {
        setProjectDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Countries data from Projects.jsx
  const countries = [
    {
      key: 'countryBulgaria',
      id: 'bulgaria',
      gradient: 'linear-gradient(to right, #FFFFFF 33.3%, #00966E 33.3% 66.6%, #D62612 66.6%)',
      projectsTitleKey: 'projectsBulgariaTitle',
    },
    {
      key: 'countryFrance',
      id: 'france',
      gradient: 'linear-gradient(to right, #0055A4 33.3%, #FFFFFF 33.3% 66.6%, #EF4135 66.6%)',
      projectsTitleKey: 'projectsFranceTitle',
    },
    {
      key: 'countryItaly',
      id: 'italy',
      gradient: 'linear-gradient(to right, #009246 33.3%, #FFFFFF 33.3% 66.6%, #CE2B37 66.6%)',
      projectsTitleKey: 'projectsItalyTitle',
    },
    {
      key: 'countryGermany',
      id: 'germany',
      gradient: 'linear-gradient(to right, #000000 33.3%, #DD0000 33.3% 66.6%, #FFCE00 66.6%)',
      projectsTitleKey: 'projectsGermanyTitle',
    },
    {
      key: 'countrySerbia',
      id: 'serbia',
      gradient: 'linear-gradient(to right, #FF0000 33.3%, #FFFFFF 33.3% 66.6%, #0000FF 66.6%)',
      projectsTitleKey: 'projectsSerbiaTitle',
    },
    {
      key: 'countryNorthMacedonia',
      id: 'northmacedonia',
      gradient: 'linear-gradient(to right, #D20000 33.3%, #FFD700 33.3% 66.6%, #D20000 66.6%)',
      projectsTitleKey: 'projectsNorthMacedoniaTitle',
    },
    {
      key: 'countryBelgium',
      id: 'belgium',
      gradient: 'linear-gradient(to right, #000000 33.3%, #FFD700 33.3% 66.6%, #FF0000 66.6%)',
      projectsTitleKey: 'projectsBelgiumTitle',
    },
    {
      key: 'countryDenmark',
      id: 'denmark',
      gradient: 'linear-gradient(135deg, #C8102E 0%, #A00D25 100%)',
      projectsTitleKey: 'projectsDenmarkTitle',
    },
  ];

  // Projects mapping - extracted from project files
  const projectsByCountry = {
    bulgaria: [
      { key: 'projectKyustendil', id: 'kyustendil', titleKey: 'kyustendilTitle', contractorKey: 'kyustendilContractor', stats: [{ label: 'projectPower', value: '117 MWp' }, { label: 'projectType', value: 'projectTypeStatic' }, { label: 'projectYear', value: '2024' }], gallery: Array.from({ length: 15 }, (_, i) => `/assets/projects/kyus${i + 1}.jpg`) },
      { key: 'projectNessebar', id: 'nessebar', titleKey: 'nessebarTitle', stats: [{ label: 'projectPower', value: '300 kWp' }, { label: 'projectType', value: 'projectTypeStatic' }, { label: 'projectYear', value: '2023' }], gallery: ['/assets/projects/dzhanny1.jpg', '/assets/projects/dzhanny2.jpg', '/assets/projects/dzhanny3.jpg', '/assets/projects/dzhanny4.jpg', '/assets/projects/dzhanny5.jpg', '/assets/projects/dzhanny6.jpg'] },
      { key: 'projectPernikOne', id: 'pernikone', titleKey: 'pernikoneTitle', contractorKey: 'pernikoneContractor', stats: [{ label: 'projectPower', value: '1 MWp' }, { label: 'projectType', value: 'projectTypeStatic' }, { label: 'projectYear', value: '2023' }], gallery: Array.from({ length: 5 }, (_, i) => `/assets/projects/pernikone${i + 1}.jpg`) },
      { key: 'projectPernikTwo', id: 'perniktwo', titleKey: 'perniktwoTitle', stats: [{ label: 'projectPower', value: '60 kWp' }, { label: 'projectType', value: 'projectTypeStatic' }, { label: 'projectYear', value: '2023' }], gallery: Array.from({ length: 2 }, (_, i) => `/assets/projects/perniktwo${i + 1}.jpg`) },
      { key: 'projectSlivnitsa', id: 'slivnitsa', titleKey: 'slivnitsaTitle', contractorKey: 'slivnitsaContractor', stats: [{ label: 'projectPower', value: '2 MWp' }, { label: 'projectType', value: 'projectTypeStatic' }, { label: 'projectYear', value: '2023' }], gallery: Array.from({ length: 5 }, (_, i) => `/assets/projects/slivnitsa${i + 1}.jpg`) },
      { key: 'projectRasovo', id: 'rasovo', titleKey: 'rasovoTitle', stats: [{ label: 'projectPower', value: '60 kWp' }, { label: 'projectType', value: 'projectTypeStatic' }, { label: 'projectYear', value: '2023' }], gallery: Array.from({ length: 4 }, (_, i) => `/assets/projects/rasovo${i + 1}.jpg`) },
    ],
    france: [
      { key: 'projectMoussoulens', id: 'moussoulens', titleKey: 'moussoulensTitle', contractorKey: 'moussoulensContractor', stats: [{ label: 'projectPower', value: '5 MWp' }, { label: 'projectType', value: 'projectTypeStatic' }, { label: 'projectYear', value: '2023' }], gallery: Array.from({ length: 4 }, (_, i) => `/assets/projects/moussoulens${i + 1}.jpg`) },
      { key: 'projectPerpignan', id: 'perpignan', titleKey: 'perpignanTitle', contractorKey: 'perpignanContractor', stats: [{ label: 'projectPower', value: '13 MWp' }, { label: 'projectType', value: 'projectTypeTracker' }, { label: 'projectYear', value: '2024' }], gallery: Array.from({ length: 6 }, (_, i) => `/assets/projects/perpignan${i + 1}.jpg`) },
      { key: 'projectStGermain', id: 'stgermain', titleKey: 'stgermainTitle', contractorKey: 'stgermainContractor', stats: [{ label: 'projectPower', value: '23 MWp' }, { label: 'projectType', value: 'projectTypeStatic' }, { label: 'projectYear', value: '2024' }], gallery: Array.from({ length: 6 }, (_, i) => `/assets/projects/stgermain${i + 1}.jpg`) },
      { key: 'projectLesMes', id: 'lesmes', titleKey: 'lesmesTitle', contractorKey: 'lesmesContractor', repoweringKey: 'lesmesNotes', stats: [{ label: 'projectPower', value: '25 MWp' }, { label: 'projectType', value: 'projectTypeStatic' }, { label: 'projectYear', value: '2024' }], gallery: ['/assets/projects/lesmees1.jpg', '/assets/projects/lesmees2.jpg', '/assets/projects/lesmees3.jpg', '/assets/projects/lesmees4.jpg', '/assets/projects/lesmees5.jpg', '/assets/projects/lesmees6.jpg'] },
      { key: 'projectSBRU', id: 'sbru', titleKey: 'sbruTitle', stats: [{ label: 'projectPower', value: '4.1 MWp' }, { label: 'projectType', value: 'projectTypeStatic' }, { label: 'projectYear', value: '2025' }], gallery: Array.from({ length: 7 }, (_, i) => `/assets/projects/SBRU${i + 1}.jpg`) },
      { key: 'projectStElix', id: 'stelix', titleKey: 'stelixTitle', stats: [{ label: 'projectPower', value: '4.15 MWp' }, { label: 'projectType', value: 'projectTypeStatic' }, { label: 'projectYear', value: '2026' }], gallery: ['/assets/projects/stelix1.jpg', '/assets/projects/stelix2.jpg', '/assets/projects/stelix3.jpg', '/assets/projects/stelix4.jpg', '/assets/projects/stelix5.jpg', '/assets/projects/stelix7.jpg', '/assets/projects/stelix8.jpg', '/assets/projects/stelix9.jpg', '/assets/projects/stelix10.jpg', '/assets/projects/stelix11.jpg'] },
    ],
    italy: [
      { key: 'projectMonreale', id: 'monreale', titleKey: 'monrealeTitle', contractorKey: 'monrealeContractor', stats: [{ label: 'projectPower', value: '70 MWp' }, { label: 'projectType', value: 'projectTypeTracker' }, { label: 'projectYear', value: '2025' }], gallery: Array.from({ length: 5 }, (_, i) => `/assets/projects/monreale${i + 1}.jpg`) },
    ],
    germany: [
      { key: 'projectGermanyDiesel', id: 'diesel-technic', titleKey: 'projectGermanyDiesel', stats: [{ label: 'projectPower', value: '1 MWp' }, { label: 'projectType', value: 'projectTypeStatic' }, { label: 'projectYear', value: '2022' }], gallery: ['/assets/projects/diesel1.jpg', '/assets/projects/diesel2.jpg', '/assets/projects/diesel4.jpg', '/assets/projects/diesel5.jpg', '/assets/projects/diesel6.jpg', '/assets/projects/diesel7.jpg', '/assets/projects/diesel9.jpg'] },
      { key: 'projectPolch', id: 'polch', titleKey: 'polchTitle', stats: [{ label: 'projectPower', value: '5 MWp' }, { label: 'projectType', value: 'projectTypeStatic' }, { label: 'projectYear', value: '2023' }], gallery: Array.from({ length: 12 }, (_, i) => `/assets/projects/polch${i + 1}.jpg`) },
    ],
    serbia: [
      { key: 'projectLapovo', id: 'lapovo', titleKey: 'lapovoTitle', stats: [{ label: 'projectPower', value: '1 MWp' }, { label: 'projectType', value: 'projectTypeStatic' }, { label: 'projectYear', value: '2022' }], gallery: Array.from({ length: 9 }, (_, i) => `/assets/projects/lapovo${i + 1}.jpg`) },
    ],
    northmacedonia: [
      { key: 'projectVitaminka', id: 'vitaminka', titleKey: 'vitaminkaTitle', stats: [{ label: 'projectPower', value: '1.5 MWp' }, { label: 'projectType', value: 'projectTypeStatic' }, { label: 'projectYear', value: '2023' }], gallery: Array.from({ length: 5 }, (_, i) => `/assets/projects/vitaminka${i + 1}.jpg`) },
    ],
    belgium: [
      { key: 'projectBelgiumMain', id: 'main', titleKey: 'projectBelgiumMain', stats: [{ label: 'projectPower', value: '1 MWp' }, { label: 'projectType', value: 'projectTypeStatic' }, { label: 'projectYear', value: '2022' }], gallery: Array.from({ length: 6 }, (_, i) => `/assets/projects/belgium${i + 1}.jpg`) },
    ],
    denmark: [
      { key: 'projectAunslev', id: 'aunslev', titleKey: 'projectAunslev', stats: [{ label: 'projectPower', value: '31 MWp' }, { label: 'projectType', value: 'projectTypeTracker' }, { label: 'projectYear', value: '2026' }], gallery: Array.from({ length: 7 }, (_, i) => `/assets/projects/Aunslev${i + 1}.jpg`) },
      { key: 'projectVillerup', id: 'villerup', titleKey: 'villerupTitle', stats: [{ label: 'projectPower', value: '12 MWp' }, { label: 'projectType', value: 'projectTypeTracker' }, { label: 'projectYear', value: '2025' }], gallery: Array.from({ length: 7 }, (_, i) => `/assets/projects/Villerup${i + 1}.jpg`) },
    ],
  };

  const [photoIndex, setPhotoIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const selectedCountry = countries.find(c => c.id === country);
  const selectedProjects = country ? projectsByCountry[country] || [] : [];
  const selectedProject = project && country ? selectedProjects.find(p => p.id === project) : null;

  const handleCountrySelect = (countryId) => {
    setSearchParams({ country: countryId });
    setCountryDropdownOpen(false);
    setProjectDropdownOpen(false);
    setSearchParams({ country: countryId }); // Clear project when country changes
  };

  const handleProjectSelect = (projectId) => {
    setSearchParams({ country, project: projectId });
    setProjectDropdownOpen(false);
  };

  const handleImageClick = (index) => {
    setPhotoIndex(index);
    setIsOpen(true);
  };

  return (
    <>
      <Helmet>
        <title>Infinity Energy Solar – Browse Projects</title>
      </Helmet>
      <div className="min-h-screen relative bg-gradient-to-b from-green-100 via-yellow-100 to-blue-100">
        {/* Background Images Layer - Above gradient, below grid */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `url('/assets/background/solarpark.png'), url('/assets/background/windturbine.png')`,
            backgroundSize: '60% auto, 40% auto',
            backgroundPosition: 'bottom right, top left',
            backgroundRepeat: 'no-repeat',
            opacity: 0.2,
            zIndex: 1
          }}
        />
        {/* Grid Pattern Overlay - On top of images */}
        <div className="grid-pattern relative" style={{ zIndex: 2 }}>
        <section className="pt-40 pb-24 px-6 lg:px-16">
          <div className="max-w-7xl mx-auto">
            {/* Dropdowns at top left */}
            <div className="flex items-start gap-4 mb-12 flex-wrap">
              {/* Country Dropdown */}
              <div className="relative" ref={countryDropdownRef}>
                <button
                  onClick={() => {
                    setCountryDropdownOpen(!countryDropdownOpen);
                    setProjectDropdownOpen(false);
                  }}
                  className="flex items-center gap-3 px-6 py-4 bg-white/70 backdrop-blur-sm rounded-xl shadow-lg hover:shadow-xl transition-all border-2 border-slate-200 hover:border-emerald-400 min-w-[200px] justify-between"
                >
                  <span className="font-bold text-slate-900">
                    {selectedCountry ? t(selectedCountry.key) : t('selectCountry')}
                  </span>
                  <ChevronDownIcon 
                    className={`w-5 h-5 text-slate-600 transition-transform ${countryDropdownOpen ? 'rotate-180' : ''}`} 
                  />
                </button>

                <AnimatePresence>
                  {countryDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute top-full left-0 mt-2 w-full bg-white/90 backdrop-blur-sm rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-[9999]"
                    >
                      <div className="max-h-96 overflow-y-auto">
                        {countries.map((countryItem) => (
                          <button
                            key={countryItem.key}
                            onClick={() => handleCountrySelect(countryItem.id)}
                            className={`w-full px-6 py-4 text-left hover:bg-emerald-50 transition-colors flex items-center gap-3 ${
                              country === countryItem.id ? 'bg-emerald-50' : ''
                            }`}
                          >
                            <span className="text-slate-900 font-bold">{t(countryItem.key)}</span>
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Project Dropdown - Only visible when country is selected */}
              {selectedCountry && (
                <div className="relative" ref={projectDropdownRef}>
                  <button
                    onClick={() => {
                      setProjectDropdownOpen(!projectDropdownOpen);
                      setCountryDropdownOpen(false);
                    }}
                    disabled={!selectedCountry || selectedProjects.length === 0}
                    className={`flex items-center gap-3 px-6 py-4 bg-white/70 backdrop-blur-sm rounded-xl shadow-lg hover:shadow-xl transition-all border-2 min-w-[200px] justify-between ${
                      !selectedCountry || selectedProjects.length === 0
                        ? 'border-slate-200 opacity-50 cursor-not-allowed'
                        : 'border-slate-200 hover:border-emerald-400'
                    }`}
                  >
                    <span className="font-bold text-slate-900">
                      {selectedProject ? t(selectedProject.key) : t('selectProject')}
                    </span>
                    <ChevronDownIcon 
                      className={`w-5 h-5 text-slate-600 transition-transform ${projectDropdownOpen ? 'rotate-180' : ''}`} 
                    />
                  </button>

                  <AnimatePresence>
                    {projectDropdownOpen && selectedProjects.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute top-full left-0 mt-2 w-full bg-white/90 backdrop-blur-sm rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-[9999]"
                      >
                        <div className="max-h-96 overflow-y-auto">
                          {selectedProjects.map((projectItem) => (
                            <button
                              key={projectItem.key}
                              onClick={() => handleProjectSelect(projectItem.id)}
                              className={`w-full px-6 py-4 text-left hover:bg-emerald-50 transition-colors ${
                                project === projectItem.id ? 'bg-emerald-50' : ''
                              }`}
                            >
                              <span className="text-slate-900 font-bold">{t(projectItem.key)}</span>
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>

            {/* Project Details */}
            {selectedProject && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-12"
              >
                {/* Info Blocks at Top */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {selectedProject.stats.map((stat, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: idx * 0.1 }}
                      className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 text-center shadow-lg"
                    >
                      <div className="text-2xl font-black text-blue-900 mb-2">
                        {stat.label === 'projectType' ? t(stat.value) : stat.value}
                      </div>
                      <div className="text-xs font-medium text-slate-700 uppercase tracking-wide">
                        {stat.label === 'projectType' ? t('projectType') : t(stat.label)}
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Project Title */}
                <div>
                  <h1 className="text-4xl md:text-5xl font-black text-blue-900 mb-4">
                    {t(selectedProject.titleKey)}
                  </h1>
                </div>

                {/* Photo Gallery */}
                <div>
                  <h2 className="text-xl font-bold text-blue-900 mb-4">{t('photoGallery')}</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {selectedProject.gallery.map((src, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.3 + idx * 0.05 }}
                        whileHover={{ scale: 1.05 }}
                        className="aspect-[16/10] rounded-2xl overflow-hidden cursor-pointer shadow-lg hover:shadow-xl transition-all"
                        onClick={() => handleImageClick(idx)}
                      >
                        <img
                          src={src}
                          alt={`${t(selectedProject.titleKey)} ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Contractor Information - After Gallery */}
                {(selectedProject.contractorKey || selectedProject.repoweringKey) && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="space-y-2"
                  >
                    {selectedProject.contractorKey && (
                      <p className="text-lg text-slate-700">{t(selectedProject.contractorKey)}</p>
                    )}
                    {selectedProject.repoweringKey && (
                      <p className="text-lg text-slate-700">{t(selectedProject.repoweringKey)}</p>
                    )}
                  </motion.div>
                )}
              </motion.div>
            )}

            {/* Empty State */}
            {!selectedProject && selectedCountry && (
              <div className="text-center py-24">
                <p className="text-xl text-slate-600">{t('selectProjectFromDropdown')}</p>
              </div>
            )}

            {!selectedCountry && (
              <div className="text-center py-24">
                <p className="text-xl text-slate-600">{t('selectCountryToBrowse')}</p>
              </div>
            )}

            {isOpen && selectedProject && (
              <Lightbox
                open={isOpen}
                close={() => setIsOpen(false)}
                index={photoIndex}
                slides={selectedProject.gallery.map((src) => ({ src }))}
                styles={{ container: { backgroundColor: 'rgba(0, 0, 0, 0.9)' } }}
              />
            )}
          </div>
        </section>
        </div>
      </div>
    </>
  );
};

export default WebProjects;
